import { useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { trpc } from "@/lib/trpc";
import { agoraSP } from "@/lib/date";

// ─── Módulo Metas & KPIs ──────────────────────────────────────────────────────

type Rede = "instagram" | "tiktok" | "youtube" | "linkedin" | "twitter";

const REDE_INFO: Record<Rede, { label: string; icon: string }> = {
  instagram: { label: "Instagram", icon: "" },
  tiktok:    { label: "TikTok",    icon: "" },
  youtube:   { label: "YouTube",   icon: "▶️" },
  linkedin:  { label: "LinkedIn",  icon: "" },
  twitter:   { label: "Twitter/X", icon: "𝕏" },
};

const PILARES_SUGERIDOS = ["Alcance", "Engajamento", "Retenção", "Crescimento de canal", "Conversão"];
const PILAR_COR: Record<string, string> = {
  "Alcance": "var(--solar-ink)", "Engajamento": "#EC4899", "Retenção": "#52525B",
  "Crescimento de canal": "#10B981", "Conversão": "#2563EB",
};
function pilarCor(p: string) { return PILAR_COR[p] ?? "var(--solar-ink)"; }

const MESES = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

const INPUT_CLS = "w-full border border-[var(--solar-line)] rounded-lg px-3 py-2.5 text-sm text-[var(--solar-ink)] placeholder-[var(--solar-mute)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-soft)] focus:border-[var(--brand)] transition bg-white";

interface Kpi {
  id: number;
  clientId: number;
  pilar: string;
  kpiNome: string;
  rede: string | null;
  ordem: number;
  valores: Record<string, { meta: string | null; realizado: string | null }>;
}

// ─── Atingimento (só para valores numéricos simples) ──────────────────────────

function parseNum(s: string | null | undefined): number | null {
  if (!s) return null;
  if (s.includes("-")) return null; // intervalo — não calcula
  const m = s.toLowerCase().replace(/\s/g, "");
  let mult = 1;
  if (m.includes("mi")) mult = 1_000_000;
  else if (m.includes("mil") || m.includes("k")) mult = 1_000;
  let clean = m.replace(/[^\d.,]/g, "");
  // formato BR (1.234,56) vs US (1,234.56): o último separador é o decimal
  const lastComma = clean.lastIndexOf(",");
  const lastDot   = clean.lastIndexOf(".");
  if (lastComma >= 0 && lastDot >= 0) {
    if (lastComma > lastDot) {
      clean = clean.replace(/\./g, "").replace(",", ".");
    } else {
      clean = clean.replace(/,/g, "");
    }
  } else if (lastComma >= 0) {
    clean = clean.replace(",", ".");
  }
  // Só dots: já está em formato JS-friendly.
  const v = parseFloat(clean);
  return isNaN(v) ? null : v * mult;
}

function atingimento(meta: string | null | undefined, real: string | null | undefined): number | null {
  const m = parseNum(meta), r = parseNum(real);
  if (m == null || r == null || m === 0) return null;
  return Math.round((r / m) * 100);
}

// ─── Célula editável ──────────────────────────────────────────────────────────

function CelulaEditavel({
  value, placeholder, onSave, color,
}: {
  value: string | null;
  placeholder: string;
  onSave: (v: string) => void;
  color?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={() => { setEditing(false); if (draft !== (value ?? "")) onSave(draft); }}
        onKeyDown={e => {
          if (e.key === "Enter") { setEditing(false); if (draft !== (value ?? "")) onSave(draft); }
          if (e.key === "Escape") setEditing(false);
        }}
        className="w-full border-2 border-[var(--brand)] rounded-md px-2 py-1 text-sm text-center focus:outline-none"
      />
    );
  }
  return (
    <button
      onClick={() => { setDraft(value ?? ""); setEditing(true); }}
      className="w-full px-2 py-1 rounded-md text-sm text-center hover:bg-[var(--solar-bg)] transition-colors"
      style={{ color: value ? (color ?? "var(--solar-ink)") : "var(--solar-mute)", fontWeight: value ? 600 : 400 }}
    >
      {value || placeholder}
    </button>
  );
}

// ─── Modal Novo KPI ───────────────────────────────────────────────────────────

function NovoKpiModal({
  clientId, onSave, onClose,
}: {
  clientId: number;
  onSave: () => void;
  onClose: () => void;
}) {
  const [pilar, setPilar]     = useState("Alcance");
  const [kpiNome, setKpiNome] = useState("");
  const [rede, setRede]       = useState<Rede | "">("");

  const createMutation = trpc.metasKpi.createKpi.useMutation({ onSuccess: () => { onSave(); onClose(); } });

  function handleSave() {
    if (!pilar.trim() || !kpiNome.trim() || createMutation.isPending) return;
    createMutation.mutate({
      clientId,
      pilar: pilar.trim(),
      kpiNome: kpiNome.trim(),
      rede: rede || undefined,
    });
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-x-4 top-24 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-md z-50 bg-white rounded-xl shadow-[var(--elev-3)] flex flex-col overflow-hidden" style={{ border: "1px solid var(--solar-line)" }}>
        <div className="flex items-center justify-between px-6 py-4 shrink-0" style={{ borderBottom: "1px solid var(--solar-line)" }}>
          <h2 className="font-bold text-base text-[var(--solar-ink)]" style={{ fontFamily: "Poppins, sans-serif" }}>Novo KPI</h2>
          <button onClick={onClose} className="text-[var(--solar-mute)] hover:text-[var(--solar-ink)] transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-[11px] font-semibold text-[var(--solar-mute)] uppercase tracking-wide mb-1.5">Pilar *</label>
            <input list="pilares-list" type="text" value={pilar} onChange={e => setPilar(e.target.value)}
              placeholder="Alcance, Engajamento…" className={INPUT_CLS} />
            <datalist id="pilares-list">
              {PILARES_SUGERIDOS.map(p => <option key={p} value={p} />)}
            </datalist>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-[var(--solar-mute)] uppercase tracking-wide mb-1.5">KPI *</label>
            <input autoFocus type="text" value={kpiNome} onChange={e => setKpiNome(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") handleSave(); }}
              placeholder="Ex: Impressões, Engajamento médio, Seguidores líquidos" className={INPUT_CLS} />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-[var(--solar-mute)] uppercase tracking-wide mb-1.5">Rede</label>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(REDE_INFO) as Rede[]).map(r => {
                const isActive = rede === r;
                return (
                  <button key={r} type="button" onClick={() => setRede(isActive ? "" : r)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border-2 transition-all"
                    style={{ background: isActive ? "var(--brand-soft)" : "white", borderColor: isActive ? "var(--brand)" : "var(--solar-line)", color: isActive ? "var(--brand-ink)" : "var(--solar-mute)" }}>
                    <span>{REDE_INFO[r].icon}</span>{REDE_INFO[r].label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        {createMutation.error && <p className="px-6 pb-2 text-red-500 text-sm">{createMutation.error.message}</p>}
        <div className="flex gap-3 px-6 py-4 shrink-0" style={{ borderTop: "1px solid var(--solar-line)" }}>
          <button onClick={onClose} className="flex-1 py-2.5 rounded-[10px] border border-[var(--solar-line)] text-[var(--solar-ink)] text-sm font-semibold hover:bg-[var(--solar-bg)] transition-colors">
            Cancelar
          </button>
          <button onClick={handleSave} disabled={!pilar.trim() || !kpiNome.trim() || createMutation.isPending}
            className="flex-1 py-2.5 rounded-[10px] shadow-[var(--elev-1)] text-white text-sm font-semibold transition-all disabled:opacity-40"
            style={{ background: "var(--solar-ink)", fontFamily: "Poppins, sans-serif" }}>
            {createMutation.isPending ? "Salvando…" : "Adicionar KPI"}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Página ───────────────────────────────────────────────────────────────────

export default function MetasKPIs() {
  const { session } = useAuth();
  const clientId = session?.activeClientId ?? session?.clientId ?? 1;
  const hoje = agoraSP();

  // derive ano a cada render — vira-virada de ano não exige reload
  const ano = agoraSP().ano;
  const [mes, setMes] = useState(hoje.mes); // 0-indexed
  const [modalOpen, setModalOpen] = useState(false);

  const periodo = `${ano}-${String(mes + 1).padStart(2, "0")}`;

  const { data: kpis = [], isLoading, refetch } = trpc.metasKpi.list.useQuery(
    { clientId }, { enabled: !!clientId }
  );
  const setValorMutation = trpc.metasKpi.setValor.useMutation({ onSuccess: () => refetch() });
  const deleteMutation   = trpc.metasKpi.deleteKpi.useMutation({ onSuccess: () => refetch() });

  const grupos = useMemo(() => {
    const g: Record<string, Kpi[]> = {};
    for (const k of kpis as Kpi[]) (g[k.pilar] ??= []).push(k);
    return g;
  }, [kpis]);

  return (
    <div className="h-full flex flex-col bg-[var(--solar-bg)]">
      {/* Header */}
      <div className="bg-white border-b border-[var(--solar-line)] px-6 py-4 shrink-0">
        <div className="flex items-center gap-4 flex-wrap gap-y-3">
          <div className="flex-1 min-w-0">
            <h1 className="font-bold text-lg text-[var(--solar-ink)]" style={{ fontFamily: "Poppins, sans-serif" }}>
              Metas & KPIs
            </h1>
            <p className="text-[var(--solar-mute)] text-xs mt-0.5">
              Estratégia de Redes Sociais {ano} — meta vs. realizado
            </p>
          </div>
          <button
            onClick={() => setModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-[10px] shadow-[var(--elev-1)] transition-all hover:opacity-90 shrink-0"
            style={{ background: "var(--solar-ink)", fontFamily: "Poppins, sans-serif" }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Novo KPI
          </button>
        </div>

        {/* Seletor de mês */}
        <div className="flex items-center gap-1 mt-3 flex-wrap">
          {MESES.map((m, i) => (
            <button
              key={m}
              onClick={() => setMes(i)}
              className="px-2.5 py-1 rounded-md text-xs font-semibold transition-all"
              style={{
                background: mes === i ? "var(--solar-ink)" : "white",
                color: mes === i ? "white" : "var(--solar-mute)",
                border: "1px solid " + (mes === i ? "var(--solar-ink)" : "var(--solar-line)"),
              }}
            >
              {m}
            </button>
          ))}
          <span className="text-xs text-[var(--solar-mute)] ml-2">{ano}</span>
        </div>
      </div>

      {/* Conteúdo */}
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-8 h-8 rounded-full border-2 border-[var(--solar-line)] border-t-[var(--solar-ink)] animate-spin" />
        </div>
      ) : kpis.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 text-[var(--solar-mute)]">
          <div className="w-16 h-16 rounded-xl flex items-center justify-center" style={{ background: "#EDEBE6" }}>
            <svg className="w-8 h-8 text-[var(--solar-ink)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <p className="text-sm font-medium">Nenhum KPI definido ainda</p>
          <button onClick={() => setModalOpen(true)} className="text-sm font-semibold text-[var(--brand)] underline underline-offset-2">
            Adicionar o primeiro KPI
          </button>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-6">
          <div className="bg-white rounded-xl border border-[var(--solar-line)] shadow-[var(--elev-1)] overflow-hidden max-w-5xl mx-auto">
            {/* Cabeçalho da tabela */}
            <div className="grid text-[10px] font-bold text-[var(--solar-mute)] uppercase tracking-widest bg-[var(--solar-bg)] border-b border-[var(--solar-line)] px-5 py-2.5"
              style={{ gridTemplateColumns: "1fr 130px 130px 110px 40px" }}>
              <span style={{ fontFamily: "Poppins, sans-serif" }}>KPI</span>
              <span className="text-center" style={{ fontFamily: "Poppins, sans-serif" }}>Meta</span>
              <span className="text-center" style={{ fontFamily: "Poppins, sans-serif" }}>Realizado</span>
              <span className="text-center" style={{ fontFamily: "Poppins, sans-serif" }}>Atingimento</span>
              <span />
            </div>

            {Object.entries(grupos).map(([pilar, lista]) => (
              <div key={pilar}>
                <div className="px-5 py-2 bg-[var(--solar-bg)] border-b border-[var(--solar-line)] flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: pilarCor(pilar) }} />
                  <span className="text-xs font-bold uppercase tracking-wide" style={{ color: pilarCor(pilar), fontFamily: "Poppins, sans-serif" }}>
                    {pilar}
                  </span>
                </div>
                {lista.map(k => {
                  const val = k.valores[periodo] ?? { meta: null, realizado: null };
                  const ating = atingimento(val.meta, val.realizado);
                  const atingCor = ating == null ? "#9CA3AF" : ating >= 100 ? "#10B981" : ating >= 70 ? "#52525B" : "#EF4444";
                  return (
                    <div key={k.id}
                      className="grid items-center px-5 py-2.5 border-b border-[var(--solar-line)] hover:bg-[var(--solar-bg)] transition-colors group"
                      style={{ gridTemplateColumns: "1fr 130px 130px 110px 40px" }}>
                      <div className="flex items-center gap-2 min-w-0">
                        {k.rede && <span className="text-sm shrink-0">{REDE_INFO[k.rede as Rede]?.icon ?? ""}</span>}
                        <span className="text-sm font-medium text-[var(--solar-ink)] truncate" style={{ fontFamily: "Poppins, sans-serif" }}>
                          {k.kpiNome}
                        </span>
                      </div>
                      <CelulaEditavel
                        value={val.meta}
                        placeholder="definir"
                        color="var(--solar-ink)"
                        onSave={v => setValorMutation.mutate({ metaKpiId: k.id, periodo, meta: v || null })}
                      />
                      <CelulaEditavel
                        value={val.realizado}
                        placeholder="—"
                        color="var(--solar-ink)"
                        onSave={v => setValorMutation.mutate({ metaKpiId: k.id, periodo, realizado: v || null })}
                      />
                      <div className="text-center">
                        {ating != null ? (
                          <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                            style={{ background: atingCor + "22", color: atingCor }}>
                            {ating}%
                          </span>
                        ) : (
                          <span className="text-xs text-[var(--solar-mute)]">—</span>
                        )}
                      </div>
                      <button
                        onClick={() => { if (confirm(`Excluir o KPI "${k.kpiNome}"?`)) deleteMutation.mutate({ id: k.id }); }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-[var(--solar-mute)] hover:text-red-500 mx-auto"
                        title="Excluir KPI"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            ))}

            <p className="px-5 py-2.5 text-[11px] text-[var(--solar-mute)] bg-[var(--solar-bg)]">
              Clique em qualquer valor de Meta ou Realizado para editar · Atingimento calculado quando os valores são numéricos
            </p>
          </div>
        </div>
      )}

      {modalOpen && (
        <NovoKpiModal
          clientId={clientId}
          onSave={() => refetch()}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}
